import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE_URL = 'https://api.wiseclean.ee/api:56Z3lZ-0';

const translations = {
    en: {
        title: 'Schedule your repeat cleaning and enjoy a 4% discount',
        address: 'Address:',
        propertyType: 'Property type:',
        date: 'Date:',
        time: 'Time:',
        comment: 'Comment:',
        createOrder: 'Create order',
        selectDate: 'Select a date',
        selectTime: 'Select a time',
        confirmation: 'Are you sure?',
        errorFetching: 'Error fetching order details.',
        errorCreating: 'Error creating order. Please try again.',
        options: 'Options:',
        requiredFields: 'Please fill out all required fields.',
        loadingOrder: 'Loading order details...'
    },
    et: {
        title: 'Planeerige oma korduv koristus ja nautige 4% allahindlust',
        address: 'Aadress:',
        propertyType: 'Kinnisvara tüüp:',
        date: 'Kuupäev:',
        time: 'Aeg:',
        comment: 'Kommentaar:',
        createOrder: 'Loo tellimus',
        selectDate: 'Valige kuupäev',
        selectTime: 'Valige aeg',
        confirmation: 'Kas olete kindel?',
        errorFetching: 'Viga tellimuse andmete hankimisel.',
        errorCreating: 'Tellimuse loomine ebaõnnestus. Proovige uuesti.',
        options: 'Valikud:',
        requiredFields: 'Palun täitke kõik nõutud väljad.',
        loadingOrder: 'Tellimuse andmeid laaditakse...'
    }
};

const RepeatOrder = () => {
    const { lang, orderId } = useParams();
    const t = translations[lang] || translations.en;

    const [orderData, setOrderData] = useState(null);
    const [orderLoading, setOrderLoading] = useState(true);
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchOrderDetails();
        fetchAvailableDates();
    }, []);

    const fetchOrderDetails = async () => {
        setOrderLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/old_order/for_repeat_order?orderID=${orderId}`);
            if (!response.ok) throw new Error(t.errorFetching);
            const data = await response.json();
            setOrderData(data);
        } catch (error) {
            console.error(error);
            alert(t.errorFetching);
        } finally {
            setOrderLoading(false);
        }
    };

    const fetchAvailableDates = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/old_order/get_dates?locale=${lang}`);
            if (!response.ok) throw new Error(t.errorFetching);
            const data = await response.json();
            setAvailableDates(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleCreateOrder = async () => {
        if (!selectedDate || !selectedTime) {
            setErrorMessage(t.requiredFields);
            return;
        }

        if (!window.confirm(t.confirmation)) return;

        setLoading(true);
        const orderPayload = {
            addressID: orderData?.AddrID,
            userID: orderData?.UserID,
            options_input: orderData?.Options_array,
            cleaningType: 'Regular',
            AddressComment: comment,
            subscriptionID: 'recr3DNXYuLwIa3tJ',
            date: selectedDate,
            time: selectedTime,
            windowsCountId: '',
            promocode: 'rech72n98Ao5a64Cr'
        };

        try {
            const response = await fetch(`${API_BASE_URL}/old_order/create_new`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderPayload)
            });

            if (!response.ok) throw new Error(t.errorCreating);
            const result = await response.json();
            window.location.href = `https://wiseclean.eu/${lang}/order-options?orderID=${result.orderID}`;
        } catch (error) {
            console.error(error);
            alert(t.errorCreating);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <h1 style={styles.title}>{t.title}</h1>

            {orderLoading ? (
                <div style={styles.loadingIndicator}>{t.loadingOrder}</div>
            ) : (
                orderData && (
                    <div style={styles.addressInfo}>
                        <p><strong>{t.address}</strong> {orderData.Addr}</p>
                        <p><strong>{t.propertyType}</strong> {orderData.ProType_join}</p>
                        <p>
                            <strong>{t.options}</strong> {lang === 'et' ? orderData.options_et : orderData.options_en}
                        </p>
                    </div>
                )
            )}

            <div style={styles.inputGroup}>
                <label style={styles.fieldLabel} htmlFor="date">{t.date}</label>
                <select
                    id="date"
                    style={styles.fieldInput}
                    value={selectedDate}
                    onChange={(e) => {
                        setSelectedDate(e.target.value);
                        if (errorMessage) setErrorMessage('');
                    }}
                    required
                >
                    <option value="">{t.selectDate}</option>
                    {availableDates.map((date) => (
                        <option key={date.date} value={date.date}>{date.date_en}</option>
                    ))}
                </select>
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.fieldLabel} htmlFor="time">{t.time}</label>
                <select
                    id="time"
                    style={styles.fieldInput}
                    value={selectedTime}
                    onChange={(e) => {
                        setSelectedTime(e.target.value);
                        if (errorMessage) setErrorMessage('');
                    }}
                    required
                >
                    <option value="">{t.selectTime}</option>
                    {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
                        '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00'
                    ].map((time) => (
                        <option key={time} value={time}>{time}</option>
                    ))}
                </select>
            </div>

            {errorMessage && (
                <p style={styles.errorMessage}>{errorMessage}</p>
            )}

            <div style={styles.inputGroup}>
                <label style={styles.fieldLabel} htmlFor="comment">{t.comment}</label>
                <textarea
                    id="comment"
                    style={styles.textarea}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add your comment here..."
                />
            </div>

            <button style={styles.button} onClick={handleCreateOrder} disabled={loading}>
                {loading ? 'Processing...' : t.createOrder}
            </button>
        </div>
    );
};

const styles = {
    container: {
        width: '100%',
        maxWidth: '400px', // Ограничение по ширине для мобильника
        margin: '0 auto',
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: 'freight-sans-pro, sans-serif'
    },
    title: {
        fontFamily: 'adelle-sans, sans-serif',
        fontSize: '32px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '20px',
        color: '#333'
    },
    loadingIndicator: {
        textAlign: 'center',
        fontSize: '18px',
        color: '#555',
        marginBottom: '20px'
    },
    addressInfo: {
        fontFamily: 'freight-sans-pro, sans-serif',
        fontSize: '19px',
        fontWeight: '400',
        color: '#333',
        marginBottom: '40px',
        backgroundColor: '#fff',
        borderRadius: '5px',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
        padding: '10px'
    },
    inputGroup: {
        marginBottom: '15px'
    },
    fieldLabel: {
        fontFamily: 'freight-sans-pro, sans-serif',
        fontSize: '19px',
        fontWeight: '700',
        display: 'block',
        marginBottom: '5px',
        color: '#333'
    },
    fieldInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        fontFamily: 'freight-sans-pro, sans-serif',
        fontSize: '19px',
        fontWeight: '400'
    },
    textarea: {
        width: '100%',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
        fontFamily: 'freight-sans-pro, sans-serif',
        fontSize: '19px',
        fontWeight: '400',
        minHeight: '80px'
    },
    errorMessage: {
        color: 'red',
        fontSize: '16px',
        marginBottom: '15px'
    },
    button: {
        width: '100%',
        padding: '12px',
        backgroundColor: 'rgb(108, 200, 217)',
        color: 'white',
        border: 'none',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        fontFamily: 'freight-sans-pro, sans-serif',
        fontSize: '19px',
        fontWeight: '700',
        cursor: 'pointer',
        marginTop: '20px'
    }
};

export default RepeatOrder;